import { defaultHoverTransition } from '@shared/utils';
import cx from 'classnames';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as Copy } from 'assets/icons/Copy.svg';
import { ReactComponent as Tick } from 'assets/icons/Tick.svg';
import useToast from 'contexts/ToastProvider/useToast';
import useCopy from 'hooks/useCopy';
import testingConstants from 'utils/testingConstants';

import useMxCookies from './hooks/useMxCookies';
import { extractUserId } from './utils';

interface UserSectionHeadingProps {
  avatarIcon: ReactNode;
}

const UserSectionHeading = ({ avatarIcon }: UserSectionHeadingProps) => {
  const { t } = useTranslation();
  const { mxDisplayName, mxUserId } = useMxCookies();
  const { pushToast } = useToast();
  const { isCopied, copy } = useCopy(mxUserId ?? '');

  const handleCopy = () => {
    if (!mxUserId || isCopied) return;
    copy().then(() => pushToast({ title: t('user_id_copied'), variant: 'success' }));
  };

  return (
    <div className="p-2 pb-0">
      <div className="bg-gray-10 rounded-2xl p-2 flex flex-col gap-2 w-[240px]">
        <div className="flex items-center gap-2">
          {avatarIcon}
          <span className="text-size-sm text-grayscale-black truncate flex-1">
            {mxDisplayName || `${t('user')}#${extractUserId(mxUserId)}`}
          </span>
        </div>
        <div
          className={cx(
            'bg-gray-30 rounded-lg p-2 flex items-center gap-2',
            isCopied ? 'cursor-default' : 'cursor-pointer hover:bg-gray-40',
            defaultHoverTransition
          )}
          onClick={handleCopy}
          data-testid={testingConstants.profileOption}
        >
          <span className="text-xs leading-normal text-grayscale-black flex-1 truncate">{mxUserId}</span>
          {isCopied ? (
            <Tick className="w-4 h-4 shrink-0 stroke-primary-50 stroke-1.5" />
          ) : (
            <Copy className="w-4 h-4 shrink-0 stroke-grayscale-black stroke-1.5" />
          )}
        </div>
      </div>
    </div>
  );
};

export default UserSectionHeading;
